import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, CalendarDays } from "lucide-react";
import themmIcon from "../assets/themm_icon.png";
import tmmWordmark from "../assets/tmm_wordmark.png";
import shopIcon from "../assets/shop.svg";
import appIcon from "../assets/app.svg";
import instagramIcon from "../assets/instagram_link.svg";
import cultureMark from "../assets/culture_mark.png";
import musicMark from "../assets/nav_music.png";
import fashionMark from "../assets/nav_fashion.png";
import artMark from "../assets/nav_art.png";
import eventsMark from "../assets/nav_events.png";
import contactMark from "../assets/nav_contact.png";
import membersMark from "../assets/nav_members.png";
import servicesMark from "../assets/nav_services.png";
import gamedevMark from "../assets/nav_gamedev.png";
import SearchOverlay from "./SearchOverlay";
import { useCart } from "../context/CartContext";

// Page-only links shown in the mobile dropdown (search/cart/instagram/app stay outside)
// Creators room, chat, news, and downloads are member-only and surfaced from the members page.
const DRAWER_ITEMS = [
  { to: "/home", label: "home", useTmm: true },
  { to: "/music", label: "music" },
  { to: "/fashion", label: "fashion" },
  { to: "/art", label: "art" },
  { to: "/events", label: "events" },
  { to: "/services", label: "services" },
  { to: "/gaming", label: "gamedev" },
  { to: "/contact", label: "contact" },
  { to: "/culture", label: "culture" },
  { to: "/members", label: "members" },
  { to: "/availability", label: "availability", mark: null },
  { to: "/shop", label: "shop", mark: null },
];

// Per-item mark class so we can size individually
const MARK_CLASS = {
  "/music": "nav-mark nav-mark-music",
  "/fashion": "nav-mark nav-mark-fashion",
  "/art": "nav-mark nav-mark-art",
  "/events": "nav-mark nav-mark-events",
  "/services": "nav-mark nav-mark-services",
  "/gaming": "nav-mark nav-mark-gamedev",
  "/contact": "nav-mark nav-mark-contact",
  "/culture": "nav-mark nav-mark-culture",
  "/members": "nav-mark nav-mark-members",
};

// Full nav for desktop
const NAV_MARKS = {
  "/music": musicMark,
  "/fashion": fashionMark,
  "/art": artMark,
  "/events": eventsMark,
  "/services": servicesMark,
  "/gaming": gamedevMark,
  "/contact": contactMark,
  "/culture": cultureMark,
  "/members": membersMark,
};

const NAV_ITEMS = [
  { to: "/music", label: "music", short: "M", mark: null },
  { to: "/fashion", label: "fashion", short: "F", mark: null },
  { to: "/art", label: "art", short: "A", mark: null },
  { to: "/events", label: "events", short: "E", mark: null },
  { to: "/services", label: "services", short: "S", mark: null },
  { to: "/gaming", label: "gamedev", short: "G", mark: null },
  { to: "/contact", label: "contact", short: "C", mark: null },
  { to: "/culture", label: "culture", short: "C*", mark: null },
  { to: "/members", label: "members", short: "P", mark: null },
];

const Header = ({ hoverColor = "rgba(222,78,80,1)", compact = false }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, openCart } = useCart();
  const navigate = useNavigate();

  const style = { "--hover-color": hoverColor };
  const linkClass = `nav-link${compact ? " wide" : ""}`;

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const openCartAndClose = () => {
    setMenuOpen(false);
    openCart();
  };
  const openSearchAndClose = () => {
    setMenuOpen(false);
    setSearchOpen(true);
  };
  // Kept for future drawer entries; safe to no-op reference.
  void openCartAndClose;
  void openSearchAndClose;

  const goTo = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <>
      <header className="tmm-header" style={style}>
        <div className="header-div">
          <nav className="nav">
            {/* Desktop / tablet horizontal nav */}
            <ul className="nav-list nav-list-desktop">
              <li className="nav-item">
                <Link to="/home" className="icon-link" aria-label="The Monde Mania Home">
                  <img src={themmIcon} alt="TMM" className="icon-tmm" />
                </Link>
              </li>
              {NAV_ITEMS.map((item) => {
                const mark = NAV_MARKS[item.to];
                const markCls = MARK_CLASS[item.to] || "nav-mark";
                return (
                  <li className="nav-item" key={item.to}>
                    <Link to={item.to} className={linkClass} aria-label={item.label}>
                      {mark ? (
                        <img src={mark} alt={item.label} className={markCls} />
                      ) : (
                        compact ? item.short : item.label
                      )}
                    </Link>
                  </li>
                );
              })}
              <li className="nav-item">
                <Link
                  to="/availability"
                  className="icon-link icon-btn"
                  aria-label="Availability calendar"
                  data-testid="nav-availability"
                >
                  <CalendarDays size={21} strokeWidth={2.2} color="#fff" />
                </Link>
              </li>
              <li className="nav-item">
                <button
                  className="icon-link icon-btn"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Open search"
                >
                  <Search size={22} strokeWidth={2.4} color="#fff" />
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  onClick={openCart}
                  className="icon-link icon-btn cart-trigger"
                  aria-label={`Open cart, ${count} item${count === 1 ? "" : "s"}`}
                >
                  <img src={shopIcon} alt="" className="icon-shop" />
                  {count > 0 && <span className="cart-badge">{count}</span>}
                </button>
              </li>
              <li className="nav-item">
                <Link to="/app" className="icon-link" aria-label="App">
                  <img src={appIcon} alt="App" className="icon-app" />
                </Link>
              </li>
              <li className="nav-item">
                <a
                  href="https://www.instagram.com/tmm.unbounded"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon-link"
                  aria-label="Instagram"
                >
                  <img src={instagramIcon} alt="Instagram" className="icon-instagram" />
                </a>
              </li>
            </ul>

            {/* Mobile compact bar */}
            <div className="nav-mobile-bar">
              <Link
                to="/home"
                className="icon-link mobile-home-link"
                aria-label="The Monde Mania Home"
                onClick={() => setMenuOpen(false)}
              >
                <img src={tmmWordmark} alt="The Monde Mania" className="mobile-home-wordmark" />
              </Link>
              <div className="nav-mobile-right">
                <button
                  className="icon-btn"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Open search"
                >
                  <Search size={24} strokeWidth={2.4} color="#fff" />
                </button>
                <button
                  type="button"
                  className="icon-btn cart-trigger"
                  onClick={openCart}
                  aria-label={`Open cart, ${count} item${count === 1 ? "" : "s"}`}
                >
                  <img src={shopIcon} alt="Cart" className="icon-shop" />
                  {count > 0 && <span className="cart-badge">{count}</span>}
                </button>
                <Link
                  to="/app"
                  className="icon-link"
                  aria-label="App"
                  onClick={() => setMenuOpen(false)}
                >
                  <img src={appIcon} alt="App" className="icon-app" />
                </Link>
                <a
                  href="https://www.instagram.com/tmm.unbounded"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon-link"
                  aria-label="Instagram"
                >
                  <img src={instagramIcon} alt="Instagram" className="icon-instagram" />
                </a>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setMenuOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu size={26} color="#fff" />
                </button>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile dropdown menu — pages only */}
      <div className={`nav-drawer ${menuOpen ? "is-open" : ""}`} role="dialog" aria-modal="true">
        <div className="nav-drawer-header">
          <span className="nav-drawer-title">Menu</span>
          <button className="icon-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <X size={24} color="#fff" />
          </button>
        </div>
        <ul className="nav-drawer-list">
          {DRAWER_ITEMS.map((item) => {
            const mark = NAV_MARKS[item.to];
            const perClass = MARK_CLASS[item.to]
              ? `drawer-mark ${MARK_CLASS[item.to].replace(/nav-mark/g, "drawer-mark").replace("drawer-mark drawer-mark", "drawer-mark").trim()}`
              : "drawer-mark";
            if (item.useTmm) {
              return (
                <li key={item.to}>
                  <button type="button" onClick={() => goTo(item.to)}>
                    <img
                      src={tmmWordmark}
                      alt={item.label}
                      className="drawer-mark drawer-mark-home"
                    />
                  </button>
                </li>
              );
            }
            return (
              <li key={item.to}>
                <button type="button" onClick={() => goTo(item.to)}>
                  {mark ? (
                    <img src={mark} alt={item.label} className={perClass} />
                  ) : (
                    <span>{item.label}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div
        className={`nav-drawer-scrim ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
